import { DateTime } from "luxon";
import type React from "react";
import {
  generateTimeSlots,
  type TimeSlotConfig,
} from "@/domains/appointment/appointments/utils/calendar-utils";

export interface DragPreview {
  startIndex: number; // Appointment start position (in 15-minute intervals)
  endIndex?: number; // Optional appointment end position (in 15-minute intervals)
  startTimeString: string; // Start time label (e.g., "9:00 AM")
  endTimeString?: string; // Optional end time label (e.g., "10:30 AM")
}

export interface TimeSlotGridProps {
  config: TimeSlotConfig;
  dragPreview?: DragPreview | null;
  onDragOver?: (e: React.DragEvent, timeIndex: number) => void;
  onDragLeave?: () => void;
  onDrop?: (e: React.DragEvent, timeIndex: number) => void;
  getTimeSlotStyles?: (timeIndex: number) => { className: string };
  children?: React.ReactNode;
  className?: string;
}

export function TimeSlotGrid({
  config,
  dragPreview,
  onDragOver,
  onDragLeave,
  onDrop,
  getTimeSlotStyles,
  children,
  className = "",
}: TimeSlotGridProps) {
  const timeSlots = generateTimeSlots(config);

  return (
    <div className={`relative ${className}`}>
      {timeSlots.map((timeSlot, timeIndex) => {
        const timeSlotStyles = getTimeSlotStyles?.(timeIndex) || {
          className: "",
        };

        return (
          <div
            key={`time-slot-${timeSlot}`}
            data-time-slot={timeIndex}
            className={`border-b border-border last:border-b-0 relative ${timeSlotStyles.className}`}
            style={{ height: `${config.hourHeight}px` }}
            onDragOver={(e) => onDragOver?.(e, timeIndex)}
            onDragLeave={onDragLeave}
            onDrop={(e) => onDrop?.(e, timeIndex)}
          >
            {/* 15-minute interval lines */}
            <div
              className="absolute left-0 right-0 h-px bg-border/30"
              style={{ top: `${config.hourHeight * 0.25}px` }}
            />
            <div
              className="absolute left-0 right-0 h-px bg-border/30"
              style={{ top: `${config.hourHeight * 0.5}px` }}
            />
            <div
              className="absolute left-0 right-0 h-px bg-border/30"
              style={{ top: `${config.hourHeight * 0.75}px` }}
            />

            {/* Appointment start preview */}
            {dragPreview && Math.floor(dragPreview.startIndex) === timeIndex && (
              <div
                className="absolute left-0 right-0 bg-blue-100 border-t-2 border-blue-400 pointer-events-none flex items-center justify-center z-50"
                style={{
                  top: `${(dragPreview.startIndex % 1) * config.hourHeight}px`,
                  height: "2px",
                }}
              >
                <span className="text-xs font-semibold text-blue-700 bg-blue-200 px-2 py-1 rounded shadow-sm">
                  {dragPreview.startTimeString}
                </span>
              </div>
            )}

            {/* Appointment end preview */}
            {dragPreview?.endIndex !== undefined &&
              Math.floor(dragPreview.endIndex) === timeIndex && (
                <div
                  className="absolute left-0 right-0 bg-green-100 border-t-2 border-green-400 pointer-events-none flex items-center justify-center z-50"
                  style={{
                    top: `${(dragPreview.endIndex % 1) * config.hourHeight}px`,
                    height: "2px",
                  }}
                >
                  <span className="text-xs font-semibold text-green-700 bg-green-200 px-2 py-1 rounded shadow-sm">
                    {dragPreview.endTimeString || "End"}
                  </span>
                </div>
              )}
          </div>
        );
      })}

      {/* Children (appointments, current time line, etc.) */}
      {children}
    </div>
  );
}

/**
 * Time column component for displaying time labels
 */
export interface TimeColumnProps {
  config: TimeSlotConfig;
  className?: string;
  headerHeight?: number;
  mobileBreakpoint?: boolean;
}

export function TimeColumn({ config, className = "", mobileBreakpoint = true }: TimeColumnProps) {
  const timeSlots = generateTimeSlots(config);

  return (
    <div className={`border-r border-border ${className}`}>
      {timeSlots.map((time) => (
        <div
          key={time}
          className={`flex items-center justify-center text-muted-foreground border-b border-border px-1 ${
            mobileBreakpoint ? "text-[10px] sm:text-xs" : "text-xs"
          }`}
          style={{ height: `${config.hourHeight}px` }}
        >
          {mobileBreakpoint ? (
            <>
              <span className="sm:hidden">{time}</span>
              <span className="hidden sm:inline">{time}</span>
            </>
          ) : (
            <span>{time}</span>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Day header component for calendar columns
 */
export interface DayHeaderProps {
  date: DateTime;
  isToday?: boolean;
  className?: string;
  height?: number;
  onClick?: (date: DateTime) => void;
}

export function DayHeader({
  date,
  isToday = false,
  className = "",
  height = 48,
  onClick,
}: DayHeaderProps) {
  const weekday = date.toFormat("ccc");
  const dayNumber = date.day;

  const handleClick = () => {
    onClick?.(date);
  };

  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      {...(onClick ? { type: "button" as const } : {})}
      className={`border-b border-border flex flex-col items-center justify-center ${
        isToday ? "bg-indigo-50 dark:bg-indigo-900/20" : ""
      } ${onClick ? "cursor-pointer hover:bg-accent" : ""} ${className}`}
      style={{ height: `${height}px` }}
      onClick={handleClick}
    >
      <div className="text-xs text-muted-foreground">{weekday}</div>
      <div
        className={`text-sm font-medium ${
          isToday ? "text-indigo-600 dark:text-indigo-400 font-bold" : "text-foreground"
        }`}
      >
        {dayNumber}
      </div>
    </Tag>
  );
}

/**
 * Multi-day time slot grid for week/3-day views
 */
export interface MultiDayTimeSlotGridProps {
  dates: DateTime[];
  config: TimeSlotConfig;
  dragPreview?: (DragPreview & { dayIndex: number }) | null;
  onDragOver?: (e: React.DragEvent, dayIndex: number, timeIndex: number) => void;
  onDragLeave?: () => void;
  onDrop?: (e: React.DragEvent, dayIndex: number, timeIndex: number) => void;
  getTimeSlotStyles?: (dayIndex: number, timeIndex: number) => { className: string };
  renderDayContent?: (date: DateTime, dayIndex: number) => React.ReactNode;
  dayClassName?: string;
}

export function MultiDayTimeSlotGrid({
  dates,
  config,
  dragPreview,
  onDragOver,
  onDragLeave,
  onDrop,
  getTimeSlotStyles,
  renderDayContent,
  dayClassName = "",
}: MultiDayTimeSlotGridProps) {
  const timeSlots = generateTimeSlots(config);

  return (
    <>
      {dates.map((date, dayIndex) => {
        const isToday = date.hasSame(DateTime.now(), "day");

        return (
          <div
            key={date.toISODate()}
            data-day-index={dayIndex}
            className={`border-r border-border last:border-r-0 ${dayClassName}`}
          >
            {/* Day Header */}
            <DayHeader date={date} isToday={isToday} />

            {/* Time Slots */}
            <div className="relative">
              {timeSlots.map((timeSlot, timeIndex) => {
                const timeSlotStyles = getTimeSlotStyles?.(dayIndex, timeIndex) || {
                  className: "",
                };
                const isDraggedOver =
                  dragPreview?.dayIndex === dayIndex &&
                  Math.floor(dragPreview?.startIndex || -1) === timeIndex;

                return (
                  <div
                    key={`${date.toISODate()}-${timeSlot}`}
                    data-time-slot={timeIndex}
                    className={`border-b border-border last:border-b-0 relative ${
                      isDraggedOver
                        ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/30"
                        : ""
                    } ${timeSlotStyles.className}`}
                    style={{ height: `${config.hourHeight}px` }}
                    onDragOver={(e) => onDragOver?.(e, dayIndex, timeIndex)}
                    onDragLeave={onDragLeave}
                    onDrop={(e) => onDrop?.(e, dayIndex, timeIndex)}
                  >
                    {/* 15-minute interval lines */}
                    <div
                      className="absolute left-0 right-0 h-px bg-border/30"
                      style={{ top: `${config.hourHeight * 0.25}px` }}
                    />
                    <div
                      className="absolute left-0 right-0 h-px bg-border/30"
                      style={{ top: `${config.hourHeight * 0.5}px` }}
                    />
                    <div
                      className="absolute left-0 right-0 h-px bg-border/30"
                      style={{ top: `${config.hourHeight * 0.75}px` }}
                    />

                    {/* Appointment start preview */}
                    {dragPreview?.dayIndex === dayIndex &&
                      dragPreview?.startIndex !== null &&
                      Math.floor(dragPreview.startIndex) === timeIndex && (
                        <div
                          className="absolute left-0 right-0 bg-blue-100 z-50 border-t-2 border-blue-400 pointer-events-none flex items-center justify-center"
                          style={{
                            top: `${(dragPreview.startIndex % 1) * config.hourHeight}px`,
                            height: "2px",
                          }}
                        >
                          <span className="text-xs font-semibold text-blue-700 bg-blue-200 px-2 py-1 rounded shadow-sm">
                            {dragPreview.startTimeString}
                          </span>
                        </div>
                      )}

                    {/* Appointment end preview */}
                    {dragPreview?.dayIndex === dayIndex &&
                      dragPreview?.endIndex !== undefined &&
                      Math.floor(dragPreview.endIndex) === timeIndex && (
                        <div
                          className="absolute left-0 right-0 bg-green-100 border-t-2 border-green-400 pointer-events-none flex items-center justify-center z-50"
                          style={{
                            top: `${(dragPreview.endIndex % 1) * config.hourHeight}px`,
                            height: "2px",
                          }}
                        >
                          <span className="text-xs font-semibold text-green-700 bg-green-200 px-2 py-1 rounded shadow-sm">
                            {dragPreview.endTimeString || "End"}
                          </span>
                        </div>
                      )}
                  </div>
                );
              })}

              {/* Day-specific content (appointments, current time line, etc.) */}
              {renderDayContent?.(date, dayIndex)}
            </div>
          </div>
        );
      })}
    </>
  );
}
